<template>
  <main
    ref="root"
    class="relative flex min-h-0 grow flex-col overflow-visible @container"
  >
    <div
      v-if="currentTimestamp && isScrolledUp && !isFixedDateSeparatorVisible"
      class="flex justify-center px-5 phone:px-2 absolute top-2 left-0 right-2 m-auto z-9"
    >
      <chat-event-timestamp
        :timestamp="currentTimestamp"
        is-clickable
        @click="onFloatingDateClick"
      />
    </div>

    <div
      v-if="activeChatEvent"
      ref="dropdownMenu"
      v-click-outside="hideContextMenu"
      class="absolute z-10"
    >
      <chat-event-list-context-menu
        :p-served="pServed"
        :event="activeChatEvent"
        :events-length="onlyMessagesEvents.length"
        :selected-file="selectedFile"
        :selected-link="selectedLink"
        :selected-message-text="selectedMessageText"
        :selected-message-html="selectedMessageHtml"
        :selected-events-ids="selectedEventsIds"
        :hide-quotes="hideQuotes ?? false"
        :hide-delete="rootEventId === activeChatEvent.eventId"
        :show-delete-for-self="room.isGroup"
        @select="toggleSelection($event)"
        @close="hideContextMenu()"
      />
    </div>
    <div class="relative my-1 mr-1 min-h-0 grow overflow-hidden">
      <chat-event-list-skeleton
        v-show="!isListReady"
        class="!absolute inset-0 z-1 h-full bg-bg-base-bg1"
      />
      <div
        ref="eventListContainer"
        class="h-full min-h-0"
        :class="{ invisible: !isListReady || isRestoringViewport }"
      >
        <virtual-scroller
          ref="scroller"
          class="h-full will-change-scroll"
          :items="filteredEvents"
          :buffer="2000"
          :item-size="CHAT_ITEM_SIZE_ESTIMATE"
          reveal-unmeasured
          key-field="eventId"
          data-test="chat-event-list-virtual-scroll"
          @resize="onScrollerResize"
        >
          <template #default="{ item, index }">
            <chat-event-list-item
              :key="item.eventId"
              v-touch:[item.eventId].long="onEventRightClick"
              :next="filteredEvents[index - 1]!"
              :following="filteredEvents[index + 1]!"
              :data-event-id="item.eventId"
              :item="item"
              :active-chat-event="activeChatEvent"
              :light="item.eventId === highlightedEventId"
              :p-served="pServed"
              :selected="selectedEventsIds.has(item.eventId) || activeChatEvent?.eventId === item.eventId"
              :is-reading-locked="isEventsReadingLocked"
              :hide-quote="hideQuotes!"
              :hide-delete="rootEventId === item.eventId"
              :unread-banner-event-id="unreadBannerEventId"
              class="w-full"
              :class="{
                'cursor-pointer': selectedEventsIds.size > 0 && (item.item.type === 'IncomingMessageEvent' || item.item.type === 'OutgoingMessageEvent')
              }"
              @scroll-to-item="onScrollToItem"
              @appointment-event-click="onAppointmentEventClick($event, item)"
              @click.right.shift.stop
              @visible="onVisible"
              @on-message-dbl-click="onStartQuote"
              @on-right-click="onEventRightClick($event, item)"
              @click="onItemClick(item)"
              @reply="onStartQuote(item.eventId)"
            />
          </template>
        </virtual-scroller>
      </div>
    </div>

    <!-- h-0 layer at list/footer boundary so FAB shadow can paint over the footer -->
    <div
      v-show="isScrolledUp"
      class="relative z-20 h-0 shrink-0 overflow-visible"
    >
      <chat-scroll-down
        :p-served="pServed"
        @click="onScrollDownClick"
        @scroll-to-mention="onScrollToMention"
        @scroll-to-reaction="onScrollToReaction"
      />
    </div>
  </main>
</template>

<script lang="ts" setup>
import { ClickOutside as vClickOutside } from '@wui/common-library'
import {
  isMessageNotReactionOrNotification,
  HistoryMessageItem,
  MessageCallReceived,
  MessageOutgoingEvent,
  MessageIncomingEvent,
  isMessageDeleted,
} from '@wui/im'
import { computed, nextTick, onActivated, onBeforeUnmount, onDeactivated, onMounted, onUnmounted, provide, ref, toRefs, useTemplateRef, watch } from 'vue'


import { useMarkUnread } from '@/features/mark-message-as-unread'
import { useMessageNavigationStack, useNavigateToMessage } from '@/features/navigate-to-message'

import { ChatEventTimestamp } from '@/entities/chat-event-timestamp'
import { useChatLazyLoad, useChatLazyLoadNewer } from '@/entities/chat-handlers'
import { chatModel } from '@/entities/messages'
import { useStoreLastReadEventIds } from '@/entities/room'

import { VirtualScroller } from '@/shared/components/virtual-scroller'
import { useCurrentUser } from '@/shared/composables/current-user'
import { useGuestAuth } from '@/shared/composables/guest-auth'
import { useChatScroll } from '@/shared/composables/use-chat-scroll'
import { usePositionPopUp } from '@/shared/composables/use-position-popup'
import { vTouch } from '@/shared/directives/v-touch'
import { Room } from '@/shared/types/store'

import { findFirstEventIdOfDay } from '../lib/find-first-event-id-of-day'
import { isMatchingFixedDateSeparatorVisible } from '../lib/is-matching-fixed-date-separator-visible'
import { useAppointmentPopup } from '../lib/use-appointment-popup'
import { useStartQuote } from '../lib/use-start-quote'

import ChatEventListContextMenu from './chat-event-list-context-menu.vue'
import ChatEventListItem from './chat-event-list-item.vue'
import ChatEventListSkeleton from './chat-event-list-skeleton.vue'
import ChatScrollDown from './chat-scroll-down.vue'


const CHAT_ITEM_SIZE_ESTIMATE = 62

const props = withDefaults(
  /* eslint-disable vue/require-default-prop -- rootEventId omitted from defaults to satisfy exactOptionalPropertyTypes */
  defineProps<{
    room: Room // TODO replace with simple PServed
    events?: HistoryMessageItem[]
    eventId?: string
    selectedEventsIds?: Set<number>
    hideQuotes?: boolean
    rootEventId?: number
    disableScrollLoading?: boolean
    disableHistoryLoading?: boolean
    preventLazyLoad?: boolean
    /** Сохранять скролл только у ViewChat в keep-alive, не у чата в звонке */
    preserveKeepAliveViewport?: boolean
  }>(),
  {
    events: () => [],
    eventId: '',
    selectedEventsIds: () => new Set(),
    hideQuotes: false,
    disableScrollLoading: false,
    disableHistoryLoading: false,
    preventLazyLoad: false,
    preserveKeepAliveViewport: false,
  },
)

const emit = defineEmits<{
  (event: 'select', value: number): void
  (event: 'ready'): void
  (event: 'scrollToTail'): void
}>()

const { selectedEventsIds, room } = toRefs(props)

const { cleanupOldRoomMessages, restoreTailViewport, getRoomHistoryViewport } = chatModel.useChatMessageStore()
const dropdownMenu = useTemplateRef<HTMLElement>('dropdownMenu')
const root = useTemplateRef<HTMLElement | null>('root')
const activeChatEvent = ref<HistoryMessageItem<MessageIncomingEvent | MessageOutgoingEvent> | null>(null)
const selectedFile = ref<string | null>(null)
const pServed = computed(() => room.value.pServedUser)
const selectedMessageText = ref('')
const selectedMessageHtml = ref('')
const eventIdOfSelectedMessageText = ref()
const currentTimestamp = ref<number | null>(null)
const isFixedDateSeparatorVisible = ref(false)
const selectedLink = ref<HTMLElement | null>(null)
const isListReady = ref(!props.preserveKeepAliveViewport)
const isFeedActive = ref(true)
const isRestoringViewport = ref(false)

const canStickToBottom = (): boolean => {
  return getRoomHistoryViewport(pServed.value).mode !== 'anchored'
}

const resolveEventItem = (itemOrEventId: HistoryMessageItem | string | number): HistoryMessageItem | undefined => {
  if (typeof itemOrEventId === 'object') {
    return itemOrEventId
  }

  const eventId = Number(itemOrEventId)
  return filteredEvents.value.find((event) => event.eventId === eventId)
}

const onEventRightClick = async (event: MouseEvent | TouchEvent, itemOrEventId: HistoryMessageItem | string | number) => {
  if (event.shiftKey) {
    return
  }
  event.preventDefault()

  const item = resolveEventItem(itemOrEventId)
  if (!item) {
    return
  }

  if ((item.item.type !== 'IncomingMessageEvent' && item.item.type !== 'OutgoingMessageEvent')) {
    return
  }
  const typedItem = item as HistoryMessageItem<MessageIncomingEvent | MessageOutgoingEvent>
  if (isMessageDeleted(typedItem)) {
    return
  }

  selectedFile.value = (event.target as HTMLElement).closest('[data-cookie]')?.getAttribute('data-cookie') ?? null
  activeChatEvent.value = typedItem
  await nextTick()

  usePositionPopUp({
    event,
    container: eventListContainer,
    popup: dropdownMenu,
  })
}

const onMouseDown = (e: MouseEvent) => {
  const target = e.target as HTMLElement

  if (e.button === 2 && target) {
    if (target.tagName === 'A' && target.attributes.getNamedItem('href')) {
      selectedLink.value = target
    } else {
      selectedLink.value = null
    }
    const eventId = getEventIdForChildElement(target)
    const selection = window.getSelection()
    const selectedText = selection?.toString()
    const eventIdOfSelection = getEventIdForChildElement(selection?.anchorNode as HTMLElement)

    if (eventId && selectedText && eventIdOfSelection === eventId) {
      eventIdOfSelectedMessageText.value = eventId
      selectedMessageText.value = selectedText
      if (selection && selection.rangeCount > 0) {
        const container = document.createElement('div')
        container.appendChild(selection.getRangeAt(0).cloneContents())
        selectedMessageHtml.value = container.innerHTML
      } else {
        selectedMessageHtml.value = ''
      }
    }
  }
}

const getEventIdForChildElement = (element: HTMLElement): string | null => {
  const eventId = element?.parentElement?.getAttribute('data-event-id')
  if (eventId) {
    return eventId
  }

  if (element?.parentElement) {
    return getEventIdForChildElement(element.parentElement)
  }

  return null
}

const hideContextMenu = () => {
  activeChatEvent.value = null
  selectedFile.value = null
  selectedMessageText.value = ''
  selectedMessageHtml.value = ''
  eventIdOfSelectedMessageText.value = null
  selectedLink.value = null
}

const toggleSelection = (eventId: HistoryMessageItem['eventId']) => {
  emit('select', eventId)
}

const onItemClick = (event: HistoryMessageItem) => {
  if (
    selectedEventsIds.value.size > 0 &&
    (event.item.type === 'IncomingMessageEvent' || event.item.type === 'OutgoingMessageEvent')
  ) {
    toggleSelection(event.eventId)
  }
}

const { isEventsReadingLocked, unlockMarkedUnread } = useMarkUnread(pServed.value)

const filteredEvents = computed(() => {
  return props.events
    ?.filter((e) =>
      isMessageNotReactionOrNotification(e) &&
    (e.item as MessageCallReceived).event !== 'call_record_completed')
})

const onlyMessagesEvents = computed(() => {
  return props.events?.filter((e) => {
    return e.item.type === 'OutgoingMessageEvent' || e.item.type === 'IncomingMessageEvent'
  })
})

const firstMessage = computed(() => {
  return filteredEvents.value.length
    ? filteredEvents.value[0]
    : undefined
})

const lastMessage = computed(() => {
  return filteredEvents.value.at(-1)
})

const targetEventId = computed(() => {
  const parsedNumberParam = parseInt(props.eventId)
  return isNaN(parsedNumberParam) ? undefined : parsedNumberParam
})

const {
  onScroll: scroll,
  scrollToItem,
  scrollToBottom,
  beginPrependLock,
  endPrependLock,
  beginAppendLock,
  endAppendLock,
  saveViewportForReuse,
  restoreViewportAfterReuse,
  cancelStickToBottom,
  checkHistoryLoadEdges,
  canLoadOlderHistory,
  isPrependScrollLocked,
  onScrollerResize,
  eventListContainer,
  isScrolledUp,
  scroller, // do not remove
} = useChatScroll({ canStickToBottom })

/**
 * virtua `shift` is auto-detected in VirtualScroller on top-prepend
 * (first key changes, last key stays). Do not keep shift during network fetch.
 */
const {
  ensureMessageLoaded,
  isAnchoredNavigation,
} = useNavigateToMessage(pServed)

const {
  hasEntries: hasQuoteNavigationEntries,
  pushFromQuote: pushQuoteNavigation,
  pop: popQuoteNavigation,
  clear: clearQuoteNavigation,
} = useMessageNavigationStack()

provide('eventListContainer', eventListContainer)

const {
  getOwnMessageRankByRoomAndContactId,
  getMarkedUnread,
} = useStoreLastReadEventIds()

const currentUserPServed = computed(() => {
  const subscriber = useCurrentUser().currentUser.value
  const guest = useGuestAuth().currentGuest.value
  if (subscriber) {
    return subscriber.imLogin
  } else if (guest) {
    return guest.pServed
  }
  return undefined
})

let scrollDownStep = 0

const unreadBannerEventId = ref<number | undefined>()
const UNREAD_BANNER_HIDE_DELAY_MS = 4000
let unreadBannerHideTimer: ReturnType<typeof setTimeout> | null = null

const getFirstUnreadEventId = (): number | undefined => {
  const markedUnread = getMarkedUnread(pServed.value)
  const lastReadRank = getOwnMessageRankByRoomAndContactId(pServed.value)

  const firstUnread = filteredEvents.value.find((event) => {
    if (!event.rank || event.item.type === 'OutgoingMessageEvent') {
      return false
    }
    if (markedUnread) {
      return event.rank >= markedUnread.rank
    }
    return event.rank > lastReadRank
  })

  return firstUnread?.eventId
}

const cancelUnreadBannerHide = () => {
  if (!unreadBannerHideTimer) {
    return
  }
  clearTimeout(unreadBannerHideTimer)
  unreadBannerHideTimer = null
}

const hideUnreadBanner = () => {
  cancelUnreadBannerHide()
  unreadBannerEventId.value = undefined
}

const scheduleUnreadBannerHide = () => {
  if (unreadBannerHideTimer || unreadBannerEventId.value === undefined) {
    return
  }
  unreadBannerHideTimer = setTimeout(() => {
    unreadBannerHideTimer = null
    if (!getFirstUnreadEventId()) {
      unreadBannerEventId.value = undefined
    }
  }, UNREAD_BANNER_HIDE_DELAY_MS)
}

const syncUnreadBannerSnapshot = (force = false) => {
  const firstUnreadEventId = getFirstUnreadEventId()
  if (!firstUnreadEventId) {
    scheduleUnreadBannerHide()
    return
  }
  cancelUnreadBannerHide()
  if (force || unreadBannerEventId.value === undefined) {
    unreadBannerEventId.value = firstUnreadEventId
  }
}

const clearUnreadBannerIfCaughtUp = () => {
  if (unreadBannerEventId.value === undefined) {
    return
  }
  if (!getFirstUnreadEventId()) {
    scheduleUnreadBannerHide()
    return
  }
  cancelUnreadBannerHide()
  const bannerEventExists = filteredEvents.value.some(
    (event) => event.eventId === unreadBannerEventId.value,
  )
  if (!bannerEventExists) {
    syncUnreadBannerSnapshot(true)
  }
}

const onScrollDownClick = async () => {
  if (!currentUserPServed.value) {
    return
  }

  if (hasQuoteNavigationEntries.value) {
    const backToEventId = popQuoteNavigation()
    if (backToEventId) {
      return scrollToEventId(backToEventId, true)
    }
  }

  const isHashNavigation = Boolean(targetEventId.value)

  if (scrollDownStep === 0 && !isHashNavigation && !isAnchoredNavigation.value) {
    const firstUnreadEventId = getFirstUnreadEventId()
    if (firstUnreadEventId) {
      scrollDownStep = 1
      syncUnreadBannerSnapshot(true)
      return scrollToEventId(firstUnreadEventId, false, { bottomIfFits: true })
    }
  }

  scrollDownStep = 0
  clearQuoteNavigation()

  if (isAnchoredNavigation.value || isHashNavigation) {
    emit('scrollToTail')
    await restoreTailViewport(pServed.value)
    await waitForScrollerLayout()
    return scrollToBottom({ stick: true, measure: true })
  }

  return scrollToLastEvent(true)
}

const onScrollToMention = async (eventId: number) => {
  await scrollToEventId(eventId, true)
}

const onScrollToReaction = async (eventId: number) => {
  await scrollToEventId(eventId, true)
}

const scrollToFirstUnreadEvent = async () => {
  const firstUnreadEventId = getFirstUnreadEventId()
  if (firstUnreadEventId) {
    // Tall unread stack → first unread at top; short stack → pin to chat bottom.
    return scrollToEventId(firstUnreadEventId, false, { bottomIfFits: true })
  }
  return scrollToLastEvent()
}

watch(isScrolledUp, (v) => {
  if (v) {
    cancelStickToBottom()
  } else {
    scrollDownStep = 0
  }
})

let listGeneration = 0
let revealRaf = 0
let pinOpenToBottomUntil = 0

const applyInitialScroll = async (settle = true) => {
  const session = listGeneration
  if (targetEventId.value) {
    await ensureMessageLoaded(targetEventId.value)
    if (session !== listGeneration) {
      return
    }
    syncUnreadBannerSnapshot()
    await scrollToEventId(targetEventId.value, true)
    return
  }

  if (!getFirstUnreadEventId()) {
    hideUnreadBanner()
    await scrollToLastEvent(false, settle)
    return
  }

  syncUnreadBannerSnapshot()
  await scrollToFirstUnreadEvent()
}

const waitForSettledFeed = (): Promise<void> => {
  const session = listGeneration
  return new Promise((resolve) => {
    const startedAt = Date.now()
    let lastScrollSize = -1
    let lastScrollOffset = -1
    let stableFrames = 0

    const tick = () => {
      if (session !== listGeneration) {
        revealRaf = 0
        resolve()
        return
      }

      const scrollSize = scroller.value?.scrollSize ?? -1
      const scrollOffset = scroller.value?.scrollOffset ?? -1
      const viewportSize = scroller.value?.viewportSize ?? 0
      if (viewportSize > 0 && scrollSize > 0) {
        const isStable = scrollSize === lastScrollSize
          && Math.abs(scrollOffset - lastScrollOffset) <= 2
        if (isStable) {
          stableFrames++
        } else {
          stableFrames = 0
          lastScrollSize = scrollSize
          lastScrollOffset = scrollOffset
        }

        if (stableFrames >= 4 && Date.now() - startedAt >= 80) {
          revealRaf = 0
          resolve()
          return
        }
      }

      if (Date.now() - startedAt >= 800) {
        revealRaf = 0
        resolve()
        return
      }

      revealRaf = requestAnimationFrame(tick)
    }

    revealRaf = requestAnimationFrame(tick)
  })
}

const revealMessageList = async () => {
  const session = listGeneration
  await nextTick()
  if (session !== listGeneration) {
    return
  }
  await applyInitialScroll(true)
  if (session !== listGeneration) {
    return
  }
  await waitForSettledFeed()
  if (session !== listGeneration) {
    return
  }
  if (!targetEventId.value && !getFirstUnreadEventId()) {
    await scrollToLastEvent(false, false)
    if (session !== listGeneration) {
      return
    }
    await waitForSettledFeed()
    if (session !== listGeneration) {
      return
    }
  }
  cancelStickToBottom()
  isListReady.value = true
}

watch(pServed, async () => {
  const session = ++listGeneration
  if (revealRaf) {
    cancelAnimationFrame(revealRaf)
    revealRaf = 0
  }
  clearQuoteNavigation()
  scrollDownStep = 0
  hideUnreadBanner()
  chatLazyLoad.clearPendingHistory()
  if (props.preserveKeepAliveViewport) {
    isListReady.value = false
  }
  await nextTick()
  if (session !== listGeneration) {
    return
  }
  await revealMessageList()
  if (session !== listGeneration || !isFeedActive.value) {
    return
  }
  enableHistoryLoading()
})

watch(
  () => getMarkedUnread(pServed.value)?.rank,
  (markedRank) => {
    if (markedRank !== undefined) {
      syncUnreadBannerSnapshot(true)
      return
    }
    clearUnreadBannerIfCaughtUp()
  },
)

watch(
  () => [
    getOwnMessageRankByRoomAndContactId(pServed.value),
    filteredEvents.value.length,
  ],
  () => {
    clearUnreadBannerIfCaughtUp()
  },
)

const HIGHLIGHT_DURATION_MS = 1500
const highlightedEventId = ref<number | null>(null)
let scrolledItemTimer: ReturnType<typeof setTimeout> | null = null
const animateScrolledItem = async (eventId: number) => {
  if (scrolledItemTimer) {
    clearTimeout(scrolledItemTimer)
    scrolledItemTimer = null
  }
  highlightedEventId.value = null
  await nextTick()
  highlightedEventId.value = eventId
  scrolledItemTimer = setTimeout(() => {
    highlightedEventId.value = null
    scrolledItemTimer = null
  }, HIGHLIGHT_DURATION_MS)
}

const scrollToLastEvent = async (force = false, settle = true) => {
  if (filteredEvents.value.length === 0) {
    return Promise.resolve()
  }
  return scrollToBottom({ force, stick: true, measure: true, settle })
}

const waitForScrollerLayout = async (): Promise<void> => {
  await nextTick()
  await nextTick()
}

const scrollToEventId = async (
  eventId: number,
  animate = false,
  options?: { bottomIfFits?: boolean },
) => {
  const id = Number(eventId)
  if (!id) {
    return Promise.resolve()
  }

  pinOpenToBottomUntil = 0
  cancelStickToBottom()

  const isLoaded = await ensureMessageLoaded(id)
  if (!isLoaded) {
    return Promise.resolve()
  }

  await waitForScrollerLayout()
  await nextTick()

  if (animate) {
    void animateScrolledItem(id)
  }

  // Settle loop inside scrollToItem — re-resolve index after measure/prepend.
  // bottomIfFits (unread): pin to bottom when the unread tail is shorter than the viewport.
  return scrollToItem(
    () => filteredEvents.value.findIndex((item) => id === Number(item.eventId)),
    {
      forceAttempts: true,
      bottomIfFits: options?.bottomIfFits ?? false,
    },
  )
}

const onScrollToItem = async (targetEventId: number, sourceEventId?: number) => {
  if (sourceEventId) {
    pushQuoteNavigation(sourceEventId, targetEventId)
  }

  await scrollToEventId(targetEventId, true)
}

defineExpose({
  scrollToBottom: scrollToLastEvent,
  scrollToEventId,
})

const { onStartQuote } = useStartQuote(pServed.value, selectedMessageText)

const updateFloatingTimestampVisibility = () => {
  const scrollEl = (scroller.value?.$el ?? null) as HTMLElement | null
  if (!scrollEl || currentTimestamp.value === null) {
    isFixedDateSeparatorVisible.value = false
    return
  }

  isFixedDateSeparatorVisible.value = isMatchingFixedDateSeparatorVisible(
    scrollEl,
    currentTimestamp.value,
  )
}

const onFloatingDateClick = async (dayTimestamp: number) => {
  const eventId = findFirstEventIdOfDay(filteredEvents.value, dayTimestamp)
  if (!eventId) {
    return
  }
  await scrollToEventId(eventId, true)
}

const onVisible = (message: HistoryMessageItem) => {
  currentTimestamp.value = message.timestamp
  updateFloatingTimestampVisibility()
}

watch(targetEventId, async (n) => {
  if (!isFeedActive.value) {
    return
  }
  const session = listGeneration
  if (!n || !targetEventId.value) {
    return
  }
  await ensureMessageLoaded(targetEventId.value)
  if (session !== listGeneration) {
    return
  }
  await scrollToEventId(targetEventId.value, true)
})

watch(firstMessage, async (n, o) => {
  if (!isFeedActive.value) {
    return
  }
  const session = listGeneration
  if (!o?.rank || !n?.rank || isPrependScrollLocked.value || isAnchoredNavigation.value) {
    return
  }

  if (!isScrolledUp.value) {
    await nextTick()
    if (session !== listGeneration) {
      return
    }
    await scrollToLastEvent()
  }
})

/**
 * Своё новое сообщение всегда ведёт в хвост ленты.
 * Чужие — только если пользователь уже был внизу.
 **/
watch(lastMessage, async (n, o) => {
  if (!isFeedActive.value || isPrependScrollLocked.value) {
    return
  }

  const isNewOwnOutgoing = n?.item.type === 'OutgoingMessageEvent' && n.eventId !== o?.eventId
  if (!isNewOwnOutgoing && (isScrolledUp.value || isAnchoredNavigation.value)) {
    return
  }

  await nextTick()
  await scrollToBottom({
    force: isNewOwnOutgoing,
    stick: true,
    measure: false,
    settle: true,
  })
})

watch(
  () => filteredEvents.value.length,
  async () => {
    // Only during the short open-to-bottom window. Stick + lastMessage handle the rest.
    if (
      props.preserveKeepAliveViewport
      || Date.now() >= pinOpenToBottomUntil
      || pinOpenToBottomUntil === 0
      || isPrependScrollLocked.value
      || isScrolledUp.value
    ) {
      return
    }
    await scrollToBottom({ force: true, stick: true, measure: false, settle: false })
  },
)

const chatLazyLoad = useChatLazyLoad(pServed.value, props.disableHistoryLoading, {
  canLoadOlderHistory,
  // Lock only for the insert frame — not during network (avoids long "frozen" scroll).
  preparePrepend: () => {
    beginPrependLock()
  },
  afterHistoryLoad: async () => {
    await nextTick()
    await endPrependLock()
  },
  onLoadCooldownEnd: () => {
    if (canLoadOlderHistory()) {
      checkHistoryLoadEdges(true)
    }
  },
})

const chatLazyLoadNewer = useChatLazyLoadNewer(pServed.value, props.disableHistoryLoading, {
  prepareAppend: () => {
    beginAppendLock()
  },
  afterAppend: async () => {
    await endAppendLock()
  },
  // Do not gate via canLoadOlderHistory — near-bottom newer load is valid in anchored mode.
  onLoadCooldownEnd: () => {
    checkHistoryLoadEdges(true)
  },
})

const { showAppointmentPopup } = useAppointmentPopup()

const onAppointmentEventClick = (
  payload: { e: MouseEvent | TouchEvent; message: string },
  event: HistoryMessageItem,
) => {
  showAppointmentPopup(payload, event as HistoryMessageItem<MessageCallReceived>)
}

watch(eventListContainer, (root, _, onCleanup) => {
  const scrollEl = (root?.querySelector('.wui-virtual-scroller') ?? root) as HTMLElement | null
  const onScrollerScroll = (event: Event) => {
    scroll(event)
    updateFloatingTimestampVisibility()
  }
  scrollEl?.addEventListener('scroll', onScrollerScroll, { passive: true })
  onCleanup(() => scrollEl?.removeEventListener('scroll', onScrollerScroll))
})

onMounted(async () => {
  const session = listGeneration
  if (props.preserveKeepAliveViewport) {
    isListReady.value = false
  }

  if (root.value) {
    root.value.addEventListener('mousedown', onMouseDown)
  }

  await nextTick()
  if (session !== listGeneration) {
    return
  }

  if (!props.preserveKeepAliveViewport) {
    isListReady.value = true
    isRestoringViewport.value = false
    pinOpenToBottomUntil = Date.now() + 3000
    syncUnreadBannerSnapshot()
    await nextTick()
    if (session !== listGeneration) {
      return
    }
    await scrollToBottom({ force: true, stick: true, measure: false, settle: true })
    if (session !== listGeneration) {
      return
    }
    enableHistoryLoading()
    emit('ready')
    return
  }

  await revealMessageList()
  if (session !== listGeneration) {
    return
  }

  enableHistoryLoading()
  checkHistoryLoadEdges(true)

  if (isEventsReadingLocked.value) {
    unlockMarkedUnread()
  }
  emit('ready')
})

watch(
  () => props.preventLazyLoad || props.disableHistoryLoading,
  (shouldDisable) => {
    if (shouldDisable) {
      chatLazyLoad.disable()
      chatLazyLoadNewer.disable()
      return
    }
    if (!isFeedActive.value) {
      return
    }
    chatLazyLoad.enable()
    chatLazyLoadNewer.enable()
  },
  { flush: 'sync' },
)

const enableHistoryLoading = () => {
  if (!props.preventLazyLoad && !props.disableHistoryLoading) {
    chatLazyLoad.enable()
    chatLazyLoadNewer.enable()
  }
}

let wasFeedDeactivated = false

onDeactivated(() => {
  if (!props.preserveKeepAliveViewport) {
    return
  }
  wasFeedDeactivated = true
  isFeedActive.value = false
  isRestoringViewport.value = true
  saveViewportForReuse((eventId) => filteredEvents.value.findIndex((event) => event.eventId === eventId))
  chatLazyLoad.disable()
  chatLazyLoadNewer.disable()
  hideContextMenu()
})

onActivated(async () => {
  if (!props.preserveKeepAliveViewport) {
    return
  }
  isFeedActive.value = true
  if (!wasFeedDeactivated) {
    const session = listGeneration
    await nextTick()
    if (session !== listGeneration) {
      return
    }
    await revealMessageList()
    if (session !== listGeneration) {
      return
    }
    isRestoringViewport.value = false
    enableHistoryLoading()
    checkHistoryLoadEdges(true)
    return
  }
  wasFeedDeactivated = false
  if (!isListReady.value) {
    const session = listGeneration
    await revealMessageList()
    if (session !== listGeneration) {
      return
    }
    isRestoringViewport.value = false
    enableHistoryLoading()
    checkHistoryLoadEdges(true)
    emit('ready')
    return
  }

  const session = listGeneration
  const reuseResult = await restoreViewportAfterReuse()
  if (session !== listGeneration) {
    return
  }

  // Left at the chat tail: follow unread/bottom rules for messages that arrived while away.
  if (reuseResult === 'follow-tail') {
    syncUnreadBannerSnapshot(true)
    await applyInitialScroll(true)
    if (session !== listGeneration) {
      return
    }
    await waitForSettledFeed()
    if (session !== listGeneration) {
      return
    }
    cancelStickToBottom()
  }

  isRestoringViewport.value = false
  enableHistoryLoading()
  if (reuseResult === 'follow-tail') {
    checkHistoryLoadEdges(true)
  }
})

onBeforeUnmount(() => {
  listGeneration++
  cancelUnreadBannerHide()
  if (revealRaf) {
    cancelAnimationFrame(revealRaf)
    revealRaf = 0
  }
  chatLazyLoad.disable()
  chatLazyLoadNewer.disable()
})

onUnmounted(() => {
  listGeneration++
  cancelUnreadBannerHide()
  if (revealRaf) {
    cancelAnimationFrame(revealRaf)
    revealRaf = 0
  }
  if (scrolledItemTimer) {
    clearTimeout(scrolledItemTimer)
    scrolledItemTimer = null
  }
  cleanupOldRoomMessages(pServed.value)
  if (!props.preventLazyLoad) {
    chatLazyLoad.disable()
    chatLazyLoadNewer.disable()
  }

  if (root.value) {
    root.value?.removeEventListener('mousedown', onMouseDown)
  }
})
</script>

<style scoped>
/*
 * Reserve space for media before decode/load. virtua hides unmeasured rows
 * (visibility: hidden), so a collapsing image reads as a blank gap.
 */
:deep([data-test='file-link-item-image']),
:deep([data-test='file-link-item-video']) {
  min-height: 120px;
}

:deep(img[data-test='file-image']) {
  min-height: 120px;
  background-color: var(--color-bg-base-bg2, rgb(0 0 0 / 4%));
}
</style>
